<template>
    <div class="idrelay-container">
        <div v-if="loading" class="loading">
            <p>Laddar formulär...</p>
        </div>

        <div v-else-if="error" class="error">
            <p>{{ error }}</p>
        </div>

        <div v-else-if="activity" class="activity-container">
            <!-- Confirmation Message -->
            <div v-if="showConfirmation" class="confirmation-view" role="status" aria-live="polite">
                <div class="confirmation-content">
                    <div v-if="props.confirmationMessage" class="confirmation-message"
                        v-html="props.confirmationMessage"></div>
                    <div v-else class="confirmation-message">
                        <h2>Tack för din anmälan!</h2>
                        <p>Din anmälan har registrerats.</p>
                    </div>

                    <button type="button" class="btn btn-primary" @click="handleBackToForm"
                        aria-label="Återgå till formuläret">
                        ← Tillbaka till formuläret
                    </button>
                </div>
            </div>

            <!-- Dynamic Form Component -->
            <DynamicForm v-else :key="formKey" :activity="activity" @submit-success="handleSubmitSuccess" />
        </div>

        <div v-else class="empty">
            <p>Formuläret kunde inte laddas.</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import type { FormActivity } from './IdRelayTypes';
import DynamicForm from './DynamicForm.vue';

interface Props {
    activityId: number;
    confirmationMessage?: string;
}

const props = defineProps<Props>();

const loading = ref(false);
const error = ref('');
const activity = ref<FormActivity | null>(null);
const showConfirmation = ref(false);
const formKey = ref(0);

const loadActivity = async () => {
    if (!props.activityId || props.activityId <= 0) {
        error.value = 'Ogiltigt aktivitets-ID';
        return;
    }

    loading.value = true;
    error.value = '';

    try {
        // Fetch activity from API
        const response = await fetch(`/api/idrelay/activity/${props.activityId}`);

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('Formuläret kunde inte hittas');
            } else if (response.status === 502) {
                throw new Error('IdRelay-tjänsten är inte tillgänglig');
            } else {
                throw new Error(`Fel vid hämtning: ${response.status}`);
            }
        }

        const data = await response.json();
        console.log('Activity data loaded:', data);
        activity.value = data;
    } catch (err) {
        error.value = err instanceof Error ? err.message : 'Kunde inte ladda formulär från API';
        console.error('Error loading IdRelay activity:', err);
    } finally {
        console.log('Loading state set to false');
        loading.value = false;
    }
};

const handleSubmitSuccess = () => {
    showConfirmation.value = true;
    // Scroll to top of confirmation message for better UX
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const handleBackToForm = () => {
    showConfirmation.value = false;
    formKey.value++; // Force form to re-render with fresh state
};

onMounted(() => {
    loadActivity();
});
</script>
